/* Pannello "Condividi": stato della pubblicazione, link copiabile, rinomina
   della parte parlante dello slug e rigenerazione del token.

   La pubblicazione è una fotografia: l'HTML viene generato qui (il rendering
   vive nel browser, coi font veri) e spedito al server, che lo conserva. Se il
   diagramma cambia dopo, il pannello lo dice e si ripubblica. */
import { api, ApiError, type ShareInfo } from "../api.js";
import type { DiagramDoc } from "@fullsteps/shared";
import { buildExportHtml } from "../export/html.js";
import { el } from "../render/svg.js";
import { modal } from "./modal.js";

const fmt = (iso: string): string => new Date(iso).toLocaleString("it-IT");

/** "flusso-urgenze-8f3k2q" → { readable: "flusso-urgenze", token: "8f3k2q" } */
function splitSlug(slug: string | null): { readable: string; token: string } {
  if (!slug) return { readable: "", token: "" };
  const i = slug.lastIndexOf("-");
  return i < 0 ? { readable: slug, token: "" } : { readable: slug.slice(0, i), token: slug.slice(i + 1) };
}

export function openShareModal(
  root: HTMLElement,
  opts: { diagramId: number; doc: DiagramDoc; share: ShareInfo; onChange: (s: ShareInfo) => void },
): void {
  const m = modal(root, "⇗ Condividi");
  let share = opts.share;

  const err = el("p", "login-err");
  err.hidden = true;
  const body = el("div", "share-body");
  m.body.append(body, err);

  const fail = (e: unknown): void => {
    err.textContent = e instanceof ApiError ? e.message : "errore di rete";
    err.hidden = false;
  };
  const run = async (fn: () => Promise<ShareInfo>): Promise<void> => {
    err.hidden = true;
    try {
      share = await fn();
      opts.onChange(share);
      draw();
    } catch (e) {
      fail(e);
    }
  };

  function draw(): void {
    body.textContent = "";
    const isPublic = share.visibility === "public";
    const { readable, token } = splitSlug(share.slug);

    // stato
    const state = el("div", "share-state");
    state.appendChild(el("span", "share-dot" + (isPublic ? " on" : "")));
    state.appendChild(
      el("span", undefined, isPublic ? "Pubblico — chiunque abbia il link può vederlo" : "Privato — visibile solo a chi ha un account"),
    );
    body.appendChild(state);

    if (!isPublic) {
      const hint = el("p", "hint", "Pubblicando si salva una fotografia del diagramma: le modifiche successive restano invisibili finché non ripubblichi.");
      const go = el("button", "btn", "Pubblica") as HTMLButtonElement;
      go.type = "button";
      go.addEventListener("click", () => {
        go.disabled = true;
        void run(() => api.publish(opts.diagramId, buildExportHtml(opts.doc))).finally(() => (go.disabled = false));
      });
      body.append(hint, go);
      return;
    }

    // link, con la parte parlante modificabile
    const url = `${location.origin}/p/`;
    const row = el("div", "share-link");
    row.appendChild(el("span", "share-url", url));
    const input = el("input", "dash-input share-slug") as HTMLInputElement;
    input.value = readable;
    input.setAttribute("aria-label", "parte modificabile del link");
    row.appendChild(input);
    row.appendChild(el("span", "share-url", `-${token}`));
    body.appendChild(row);

    const actions = el("div", "share-actions");
    const save = el("button", "btn sm", "Salva nome") as HTMLButtonElement;
    save.type = "button";
    save.addEventListener("click", () => {
      void run(() => api.updateShare(opts.diagramId, { slug: input.value.trim() }));
    });

    const copy = el("button", "btn sm", "Copia link") as HTMLButtonElement;
    copy.type = "button";
    copy.addEventListener("click", () => {
      void navigator.clipboard.writeText(`${url}${share.slug ?? ""}`).then(() => {
        copy.textContent = "Copiato ✓";
        setTimeout(() => (copy.textContent = "Copia link"), 1500);
      });
    });

    const regen = el("button", "btn sm ghost", "Rigenera link") as HTMLButtonElement;
    regen.type = "button";
    let armed = false;
    regen.addEventListener("click", () => {
      if (!armed) {
        armed = true;
        regen.textContent = "Il vecchio link smetterà di funzionare — confermi?";
        setTimeout(() => {
          armed = false;
          regen.textContent = "Rigenera link";
        }, 4000);
        return;
      }
      void run(() => api.updateShare(opts.diagramId, { regenerate: true }));
    });
    actions.append(save, copy, regen);
    body.appendChild(actions);

    // fotografia: quando, e se è invecchiata
    const meta = el("div", "share-meta");
    meta.appendChild(
      el("span", undefined, share.publishedAt ? `Pubblicato il ${fmt(share.publishedAt)} (versione ${share.publishedVersionId ?? "?"})` : "Pubblicato"),
    );
    if (share.stale) meta.appendChild(el("span", "share-stale", "il diagramma è cambiato dopo la pubblicazione"));
    body.appendChild(meta);

    const republish = el("button", "btn sm", share.stale ? "Ripubblica" : "Aggiorna fotografia") as HTMLButtonElement;
    republish.type = "button";
    republish.addEventListener("click", () => {
      republish.disabled = true;
      void run(() => api.publish(opts.diagramId, buildExportHtml(opts.doc), input.value.trim() || undefined)).finally(
        () => (republish.disabled = false),
      );
    });

    const stop = el("button", "btn sm danger", "Rendi privato") as HTMLButtonElement;
    stop.type = "button";
    stop.addEventListener("click", () => {
      void run(() => api.unpublish(opts.diagramId));
    });
    const bottom = el("div", "share-actions");
    bottom.append(republish, stop);
    body.appendChild(bottom);
  }

  draw();
}
